import React, { useContext, useEffect, useState, useRef } from 'react';
import {
  Box, Button, TextField, Grid, Typography, Avatar, Chip, Card, CardContent,
  Divider, IconButton, InputAdornment, Paper, Snackbar, Alert,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CakeIcon from '@mui/icons-material/Cake';
import GroupIcon from '@mui/icons-material/Group';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import LanguageIcon from '@mui/icons-material/Language';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import GavelIcon from '@mui/icons-material/Gavel';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import imageCompression from 'browser-image-compression';
import axios from 'axios';
import Office from './Office';
import { decrypt, encrypt } from '../../../Security';
import { AuthContext } from '../../Auth/AuthContext';

const VV = {
  primary: '#3F2B96', secondary: '#7F5AF0', accent: '#00C896',
  bg: '#F7F8FC', card: '#FFFFFF', border: 'rgba(63,43,150,0.10)',
  shadow: '0 8px 24px rgba(0,0,0,0.08)', shadowHover: '0 16px 48px rgba(0,0,0,0.13)', radius: 18,
};

const SectionCard = ({ title, icon, children, action }) => (
  <Card sx={{ borderRadius: `${VV.radius}px`, boxShadow: VV.shadow, border: `1px solid ${VV.border}`, mb: 3, bgcolor: VV.card, transition: 'box-shadow 0.3s', '&:hover': { boxShadow: VV.shadowHover } }}>
    <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: `linear-gradient(135deg, ${VV.primary}, ${VV.secondary})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}>
            {icon}
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: VV.primary, fontSize: '1rem' }}>{title}</Typography>
        </Box>
        {action}
      </Box>
      <Divider sx={{ mb: 3, borderColor: VV.border }} />
      {children}
    </CardContent>
  </Card>
);

const VVField = ({ startIcon, accentColor, ...props }) => {
  const color = accentColor || VV.secondary;
  return (
    <TextField variant="outlined" fullWidth size="small"
      InputProps={startIcon ? { startAdornment: <InputAdornment position="start">{React.cloneElement(startIcon, { sx: { color, fontSize: 18 } })}</InputAdornment> } : undefined}
      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#fafafa', transition: 'box-shadow 0.2s', '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: color }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: color, borderWidth: 2 }, '&.Mui-focused': { boxShadow: `0 0 0 3px ${color}18` } }, '& .MuiInputLabel-root.Mui-focused': { color } }}
      {...props}
    />
  );
};

const StatBox = ({ value, label }) => (
  <Box sx={{ textAlign: 'center', px: { xs: 1.5, md: 2.5 } }}>
    <Typography variant="h5" sx={{ fontWeight: 800, color: VV.primary, lineHeight: 1 }}>{value}</Typography>
    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
  </Box>
);

const RepresentativeData = ({ resetData, repId, setRepId, agendas, setAgendas, removeAgenda }) => {
  const [userId, setUserId] = useState('');
  const [name, setName] = useState('');
  const [position, setPosition] = useState('');
  const [term, setTerm] = useState('');
  const [adminArea, setAdminArea] = useState('');
  const [electedArea, setElectedArea] = useState('');
  const [email, setEmail] = useState('');
  const [dob, setDob] = useState('');
  const [pob, setPob] = useState('');
  const [party, setParty] = useState('');
  const [state, setState] = useState('');
  const [followers, setFollowers] = useState('');
  const [socialMedia, setSocialMedia] = useState({ linkedin: '', twitter: '', facebook: '', instagram: '', website: '' });
  const [officeLocations, setOfficeLocations] = useState([]);
  const [base64Image, setBase64Image] = useState('');
  const [errors, setErrors] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const fileInputRef = useRef(null);
  const { isAdmin } = useContext(AuthContext);

  const mark = () => setIsDirty(true);
  const closeSnack = () => setSnackbar(s => ({ ...s, open: false }));
  const showSnack = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const handleImageChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > 20 * 1048576) { showSnack('Oversized image. Max 20MB.', 'error'); return; }
    try {
      const compressed = await imageCompression(file, { maxSizeMB: 1, maxWidthOrHeight: 200, useWebWorker: true });
      const reader = new FileReader();
      reader.onloadend = () => { setBase64Image(reader.result.split(',')[1]); mark(); };
      reader.readAsDataURL(compressed);
    } catch (e) { showSnack('Image compression failed', 'error'); }
    event.target.value = '';
  };

  const resetAll = async () => {
    if (resetData) await resetData();
    if (setRepId) setRepId('');
    setUserId(''); setName(''); setPosition(''); setTerm('');
    setAdminArea(''); setElectedArea(''); setEmail(''); setDob(''); setPob('');
    setParty(''); setState(''); setFollowers('');
    setSocialMedia({ linkedin: '', twitter: '', facebook: '', instagram: '', website: '' });
    setOfficeLocations([]); setBase64Image(''); setErrors({}); setIsDirty(false);
  };

  const addOfficeLocation = () => { setOfficeLocations(loc => [...loc, { contact: '', address: '', email_id: '' }]); mark(); };
  const removeOfficeLoc = loc => { setOfficeLocations(officeLocations.filter(ol => ol !== loc)); mark(); };

  const uploadData = async () => {
    const id = repId || window.location.href.split('/').pop();
    let repObj = {};
    if (userId) repObj.user_id = userId.trim();
    repObj.name = name; repObj.imagePath = base64Image;
    repObj.current_elected_position = position; repObj.administrative_area = adminArea;
    repObj.elected_area = electedArea; repObj.official_email_id = email;
    repObj.dob = dob; repObj.pob = pob; repObj.party = party; repObj.state = state;
    repObj.political_positions = agendas; repObj.term_serving = term;
    repObj.followers_id = followers && followers.trim() !== '' ? followers.split(',').map(f => f.trim()) : [];
    repObj.social_media_handles = [
      { platform: 'twitter', handle_id: (!socialMedia.twitter || socialMedia.twitter.trim() === '') ? 'N/A' : socialMedia.twitter.trim() },
      { platform: 'facebook', handle_id: (!socialMedia.facebook || socialMedia.facebook.trim() === '') ? 'N/A' : socialMedia.facebook.trim() },
      { platform: 'linkedin', handle_id: (!socialMedia.linkedin || socialMedia.linkedin.trim() === '') ? 'N/A' : socialMedia.linkedin.trim() },
      { platform: 'instagram', handle_id: (!socialMedia.instagram || socialMedia.instagram.trim() === '') ? 'N/A' : socialMedia.instagram.trim() },
      { platform: 'website', handle_id: (!socialMedia.website || socialMedia.website.trim() === '') ? 'N/A' : socialMedia.website.trim() },
    ];
    repObj.office_locations = officeLocations;
    try {
      const response = await fetch(process.env.REACT_APP_PUBLIC_SERVER_URI + '/adminWeb/reps', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: encrypt({ repObj, repId: id }),
      });
      const data = decrypt(await response.json());
      if (data.msg) { showSnack(data.msg, 'success'); setIsDirty(false); }
      else showSnack(JSON.stringify(data.error), 'error');
    } catch (e) { showSnack('Network error: ' + e.message, 'error'); }
  };

  const updateRepData = async () => {
    try {
      const id = repId || window.location.href.split('/').pop();
      const response = await axios.get(process.env.REACT_APP_PUBLIC_SERVER_URI + '/adminWeb/reps/' + id, { withCredentials: true });
      const data = decrypt(response.data);
      const myrep = data.reps;
      if (myrep._id !== id && setRepId) setRepId(myrep._id);
      setUserId(myrep.user_id || ''); setName(myrep.name || '');
      setPosition(myrep.current_elected_position || ''); setTerm(myrep.term_serving || '');
      setAdminArea(myrep.administrative_area || ''); setElectedArea(myrep.elected_area || '');
      setEmail(myrep.official_email_id || ''); setDob(myrep.dob || ''); setPob(myrep.pob || '');
      setParty(myrep.party || ''); setState(myrep.state || '');
      setFollowers((myrep.followers_id || []).join(','));
      setBase64Image(myrep.imagePath || '');
      let obj = { linkedin: 'N/A', twitter: 'N/A', facebook: 'N/A', instagram: 'N/A', website: 'N/A' };
      (myrep.social_media_handles || []).forEach(({ platform, handle_id }) => { obj[platform] = handle_id; });
      setSocialMedia(obj); setOfficeLocations(myrep.office_locations || []);
      setIsDirty(false);
    } catch (error) { console.error('Error:', error); }
  };

  useEffect(() => {
    const id = repId || window.location.href.split('/').pop();
    if (id) updateRepData();
  }, [repId]);

  const validate = () => {
    const tempErrors = {
      name: name.trim() !== '' ? '' : 'This field is required.',
      current_elected_position: position.trim() !== '' ? '' : 'This field is required.',
      administrative_area: adminArea.trim() !== '' ? '' : 'This field is required.',
      official_email_id: email.trim() !== '' ? '' : 'This field is required.',
    };
    setErrors(tempErrors);
    return Object.values(tempErrors).every(x => x === '');
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (validate()) uploadData();
    else showSnack('Please fill all required fields', 'error');
  };

  const followerCount = followers ? followers.split(',').filter(f => f.trim()).length : 0;

  const SOCIAL = [
    { key: 'website', label: 'Website', icon: <LanguageIcon />, color: '#1565C0' },
    { key: 'linkedin', label: 'LinkedIn', icon: <LinkedInIcon />, color: '#0077B5' },
    { key: 'facebook', label: 'Facebook', icon: <FacebookIcon />, color: '#1877F2' },
    { key: 'twitter', label: 'Twitter / X', icon: <TwitterIcon />, color: '#000000' },
    { key: 'instagram', label: 'Instagram', icon: <InstagramIcon />, color: '#E1306C' },
  ];

  const currentRepId = repId || window.location.href.split('/').pop();

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ bgcolor: VV.bg, minHeight: '100vh', pb: 14 }}>
      <Box sx={{ background: `linear-gradient(135deg, ${VV.primary} 0%, ${VV.secondary} 100%)`, pt: 5, pb: 10, px: { xs: 2, md: 5 } }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <WorkspacePremiumIcon sx={{ color: VV.accent }} />
            <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.75)', letterSpacing: 2 }}>Representative Profile</Typography>
          </Box>
          <Typography variant="h4" sx={{ color: '#fff', fontWeight: 800 }}>{name || 'Profile Editor'}</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.65)', mt: 0.5, fontSize: '0.9rem' }}>{position || 'Manage your representative profile'}</Typography>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 }, mt: -6 }}>
        {/* Profile Header Card */}
        <Card sx={{ borderRadius: `${VV.radius}px`, boxShadow: '0 20px 60px rgba(63,43,150,0.18)', mb: 3 }}>
          <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md="auto" sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', md: 'flex-start' }, gap: 1 }}>
                <Box sx={{ position: 'relative', width: 120, height: 120 }}>
                  <Avatar
                    src={base64Image ? `data:image/jpeg;base64,${base64Image}` : undefined}
                    sx={{ width: 120, height: 120, border: `4px solid ${VV.secondary}`, boxShadow: `0 0 0 4px ${VV.bg}`, fontSize: 44, fontWeight: 700, bgcolor: `${VV.primary}22`, color: VV.primary }}
                  >
                    {!base64Image && (name ? name[0].toUpperCase() : <PersonIcon sx={{ fontSize: 50 }} />)}
                  </Avatar>
                  <IconButton onClick={() => fileInputRef.current?.click()} size="small"
                    sx={{ position: 'absolute', bottom: 2, right: 2, bgcolor: VV.accent, color: '#fff', width: 32, height: 32, boxShadow: '0 2px 8px rgba(0,200,150,0.4)', '&:hover': { bgcolor: '#00a87e', transform: 'scale(1.1)' }, transition: 'all 0.2s' }}>
                    <CameraAltIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageChange} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="outlined" onClick={() => fileInputRef.current?.click()}
                    sx={{ borderRadius: '8px', fontSize: '0.7rem', borderColor: VV.secondary, color: VV.secondary }}>Upload</Button>
                  {base64Image && (
                    <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon sx={{ fontSize: 14 }} />}
                      onClick={() => { setBase64Image(''); mark(); }} sx={{ borderRadius: '8px', fontSize: '0.7rem' }}>Remove</Button>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md>
                <Typography variant="h4" sx={{ fontWeight: 800, color: VV.primary, mb: 0.5, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                  {name || <span style={{ color: '#aaa' }}>Representative Name</span>}
                </Typography>
                <Typography variant="subtitle1" sx={{ color: '#666', mb: 2 }}>{position || <span style={{ color: '#ccc' }}>Current Position</span>}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  {party && <Chip icon={<GavelIcon sx={{ fontSize: 14 }} />} label={party} size="small" sx={{ bgcolor: '#EDE7F6', color: VV.primary, fontWeight: 600, borderRadius: '8px' }} />}
                  {state && <Chip icon={<LocationOnIcon sx={{ fontSize: 14 }} />} label={state} size="small" sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 600, borderRadius: '8px' }} />}
                  {adminArea && <Chip icon={<BusinessIcon sx={{ fontSize: 14 }} />} label={adminArea} size="small" sx={{ bgcolor: '#E3F2FD', color: '#1565C0', fontWeight: 600, borderRadius: '8px' }} />}
                  {term && <Chip label={`Term: ${term}`} size="small" sx={{ bgcolor: '#FFF8E1', color: '#F57F17', fontWeight: 600, borderRadius: '8px' }} />}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', borderTop: `1px solid ${VV.border}`, pt: 2, gap: 1 }}>
                  <StatBox value={followerCount} label="Followers" />
                  <Divider orientation="vertical" flexItem sx={{ borderColor: VV.border }} />
                  <StatBox value={agendas ? agendas.length : 0} label="Agenda" />
                  <Divider orientation="vertical" flexItem sx={{ borderColor: VV.border }} />
                  <StatBox value={officeLocations.length} label="Offices" />
                </Box>
              </Grid>

              <Grid item xs={12} md="auto">
                <Box sx={{ bgcolor: VV.bg, borderRadius: '12px', p: 2, border: `1px solid ${VV.border}`, minWidth: 160 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Profile ID</Typography>
                  <Typography sx={{ fontFamily: 'monospace', color: '#555', wordBreak: 'break-all', fontSize: '0.72rem', lineHeight: 1.6 }}>{currentRepId || '—'}</Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Personal Information */}
        <SectionCard title="Personal Information" icon={<PersonIcon sx={{ fontSize: 18 }} />}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
              <VVField label="Full Name *" startIcon={<PersonIcon />} value={name}
                onChange={e => { setName(e.target.value); mark(); }} error={!!errors.name} helperText={errors.name} />
            </Grid>
            <Grid item xs={12} md={6}>
              <VVField label="Current Elected Position *" startIcon={<GavelIcon />} value={position}
                onChange={e => { setPosition(e.target.value); mark(); }} error={!!errors.current_elected_position} helperText={errors.current_elected_position} />
            </Grid>
            <Grid item xs={12} md={6}>
              <VVField label="Term Serving" value={term} onChange={e => { setTerm(e.target.value); mark(); }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <VVField label="Administrative Area *" startIcon={<BusinessIcon />} value={adminArea}
                onChange={e => { setAdminArea(e.target.value); mark(); }} error={!!errors.administrative_area} helperText={errors.administrative_area} />
            </Grid>
            <Grid item xs={12} md={6}>
              <VVField label="Elected Area" startIcon={<LocationOnIcon />} value={electedArea} onChange={e => { setElectedArea(e.target.value); mark(); }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <VVField label="State" startIcon={<LocationOnIcon />} value={state} onChange={e => { setState(e.target.value); mark(); }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <VVField label="Party Name" startIcon={<GavelIcon />} value={party} onChange={e => { setParty(e.target.value); mark(); }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <VVField label="Date of Birth" startIcon={<CakeIcon />} value={dob} onChange={e => { setDob(e.target.value); mark(); }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <VVField label="Place of Birth" startIcon={<LocationOnIcon />} value={pob} onChange={e => { setPob(e.target.value); mark(); }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <VVField label="Official Email *" startIcon={<EmailIcon />} value={email}
                onChange={e => { setEmail(e.target.value); mark(); }} error={!!errors.official_email_id} helperText={errors.official_email_id} />
            </Grid>
            <Grid item xs={12} md={6}>
              <VVField label="Followers ID (comma-separated)" startIcon={<GroupIcon />} value={followers}
                onChange={e => { setFollowers(e.target.value); mark(); }}
                helperText={`${followerCount} follower${followerCount !== 1 ? 's' : ''} linked`} />
            </Grid>
            {isAdmin && (
              <Grid item xs={12} md={6}>
                <VVField label="User ID (Profile Admin _id)" value={userId} onChange={e => { setUserId(e.target.value); mark(); }} />
              </Grid>
            )}
          </Grid>
        </SectionCard>

        {/* Social Media */}
        <SectionCard title="Social Media Handles" icon={<LanguageIcon sx={{ fontSize: 18 }} />}>
          <Grid container spacing={2.5}>
            {SOCIAL.map(({ key, label, icon, color }) => {
              const val = socialMedia[key] === 'N/A' ? '' : (socialMedia[key] || '');
              return (
                <Grid item xs={12} md={6} key={key}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {React.cloneElement(icon, { sx: { color, fontSize: 20 } })}
                    </Box>
                    <TextField variant="outlined" fullWidth size="small" label={label} value={val}
                      onChange={e => { setSocialMedia(old => ({ ...old, [key]: e.target.value })); mark(); }}
                      placeholder="https://"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#fafafa', '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: color }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: color, borderWidth: 2 } }, '& .MuiInputLabel-root.Mui-focused': { color } }}
                    />
                    {val && (
                      <IconButton size="small" href={val.startsWith('http') ? val : `https://${val}`} target="_blank" rel="noopener"
                        sx={{ color, flexShrink: 0, '&:hover': { bgcolor: `${color}15` } }}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </SectionCard>

        {/* Office Locations */}
        <SectionCard title="Office Locations" icon={<BusinessIcon sx={{ fontSize: 18 }} />}
          action={
            <Button startIcon={<AddIcon />} onClick={addOfficeLocation} variant="contained" size="small"
              sx={{ borderRadius: '10px', background: VV.accent, boxShadow: '0 4px 12px rgba(0,200,150,0.3)', fontWeight: 700, '&:hover': { background: '#00a87e' } }}>
              Add Office
            </Button>
          }>
          {officeLocations.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <BusinessIcon sx={{ fontSize: 56, color: '#ccc', mb: 2, display: 'block', mx: 'auto' }} />
              <Typography color="text.secondary" sx={{ mb: 2 }}>No office locations added yet</Typography>
              <Button startIcon={<AddIcon />} onClick={addOfficeLocation} variant="outlined" sx={{ borderRadius: '10px', borderColor: VV.accent, color: VV.accent }}>Add First Office</Button>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {officeLocations.map((office, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Office office={office} officeLocations={officeLocations} setOfficeLocations={setOfficeLocations} index={index} removeOfficeLoc={removeOfficeLoc} />
                </Grid>
              ))}
            </Grid>
          )}
        </SectionCard>
      </Box>

      {/* Sticky Action Bar */}
      <Paper elevation={0} sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1000, borderTop: `1px solid ${VV.border}`, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(255,255,255,0.92)', py: 1.5, px: { xs: 2, md: 4 }, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
        {isDirty && <Chip label="Unsaved Changes" size="small" sx={{ bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 700, borderRadius: '8px' }} />}
        <Button startIcon={<RefreshIcon />} onClick={resetAll} variant="outlined"
          sx={{ borderRadius: '10px', borderColor: VV.border, color: '#666', '&:hover': { borderColor: '#aaa', bgcolor: '#f5f5f5' } }}>Reset</Button>
        <Button type="submit" startIcon={<SaveIcon />} onClick={handleSubmit} variant="contained" disabled={!isDirty}
          sx={{ borderRadius: '10px', px: 3.5, py: 1, fontWeight: 700, background: isDirty ? `linear-gradient(135deg, ${VV.primary}, ${VV.secondary})` : undefined, boxShadow: isDirty ? '0 4px 16px rgba(63,43,150,0.35)' : undefined, transition: 'all 0.25s', '&:hover': { boxShadow: '0 6px 24px rgba(63,43,150,0.45)' } }}>
          Save Changes
        </Button>
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={3500} onClose={closeSnack} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={closeSnack} sx={{ borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', fontWeight: 600 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RepresentativeData;
